import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogReactTranslationRef } from '@backstage/plugin-catalog-react';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  UserSettingsPage,
  SettingsLayout,
  UserSettingsGeneral,
} from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CatalogPage } from './components/catalog/CatalogPage';
import { CollapsibleFilters } from './components/catalog/CollapsibleFilters';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import {
  SpassuCodePage,
  SpassuCodeRoutesPage,
  WorkerTokenSettingsTab,
} from '@internal/plugin-spassu-code';
import { OrquestraPage } from '@internal/plugin-orquestra';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import { spassuTheme } from './themes/spassuTheme';
import {
  configApiRef,
  githubAuthApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { createTranslationMessages } from '@backstage/core-plugin-api/alpha';
import { userSettingsTranslationRef } from '@backstage/plugin-user-settings/alpha';
import { HomePage } from './components/home';
import { GestaoFluxoPage } from '@internal/plugin-gestao-fluxo';

// Descritores estáveis (referência única no módulo). Necessário para o `auto`
// sign-in do SignInPage funcionar — passar um objeto literal a cada render
// remonta o efeito interno e cancela a tentativa silenciosa.
const MICROSOFT_PROVIDER = {
  id: 'microsoft-auth-provider',
  title: 'Azure Entra ID',
  message: 'Entrar com conta corporativa',
  apiRef: microsoftAuthApiRef,
};

const GITHUB_PROVIDER = {
  id: 'github-auth-provider',
  title: 'GitHub',
  message: 'Entrar com conta GitHub',
  apiRef: githubAuthApiRef,
};

const app = createApp({
  apis,
  __experimentalTranslations: {
    availableLanguages: ['en', 'pt-BR'],
    defaultLanguage: 'pt-BR',
    resources: [
      createTranslationMessages({
        ref: userSettingsTranslationRef,
        full: false,
        messages: {
          'settingsLayout.title': 'Configurações',
          sidebarTitle: 'Configurações',
          'languageToggle.title': 'Idioma',
          'languageToggle.description': 'Alterar o idioma',
          'themeToggle.title': 'Tema',
          'themeToggle.description': 'Alterar o modo do tema',
          'themeToggle.names.light': 'Claro',
          'themeToggle.names.dark': 'Escuro',
          'themeToggle.names.auto': 'Automático',
          'signOutMenu.title': 'Sair',
          'pinToggle.title': 'Fixar Sidebar',
          'pinToggle.description': 'Impedir que a sidebar recolha',
          'identityCard.title': 'Identidade Backstage',
          'profileCard.title': 'Perfil',
          'appearanceCard.title': 'Aparência',
          'authProviders.title': 'Provedores Disponíveis',
          'defaultSettingsPage.tabsTitle.general': 'Geral',
          'defaultSettingsPage.tabsTitle.authProviders':
            'Provedores de Autenticação',
          'defaultSettingsPage.tabsTitle.featureFlags': 'Feature Flags',
        },
      }),
      createTranslationMessages({
        ref: catalogReactTranslationRef,
        full: false,
        messages: {
          'catalogFilter.title': 'Filtros',
          'catalogFilter.buttonTitle': 'Filtros',
        },
      }),
    ],
  },
  themes: [
    {
      id: 'spassu-theme',
      title: 'Spassu Custom Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={spassuTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    // SignInPage: props => <SignInPage {...props} auto providers={['guest']} />,
    // Provider único escolhido pela flag `spassuCode.scm.providers.github.enabled`
    // (env GITHUB_SCM_PROVIDER_ENABLED). true → GitHub; false (default) → Azure
    // Entra ID. Mesma variável que controla o registro do SCM/board GitHub.
    SignInPage: props => {
      const config = useApi(configApiRef);
      const useGithub =
        config.getOptionalBoolean('spassuCode.scm.providers.github.enabled') ??
        false;
      return (
        <SignInPage
          {...props}
          auto
          provider={useGithub ? GITHUB_PROVIDER : MICROSOFT_PROVIDER}
        />
      );
    },
  },
});

const catalogColumns = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  CatalogTable.columns.createNameColumn(),
  {
    title: 'Descrição',
    field: 'entity.metadata.description',
    highlight: false,
    width: '30%',
    cellStyle: {
      maxWidth: 300,
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
  },
  CatalogTable.columns.createOwnerColumn(),
];

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    {/* CatalogIndexPage registra o routeRef `catalog`; a página customizada entra como outlet */}
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CatalogPage
        columns={catalogColumns}
        tableOptions={{
          padding: 'dense',
          pageSize: 20,
          pageSizeOptions: [10, 20, 50],
        }}
        filters={<CollapsibleFilters />}
      />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage initialFilter="all" />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout>
        <SettingsLayout.Route path="general" title="General">
          <UserSettingsGeneral />
        </SettingsLayout.Route>
        <SettingsLayout.Route path="worker-token" title="Worker Token">
          <WorkerTokenSettingsTab />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/spassu-code" element={<SpassuCodePage />} />
    <Route path="/spassu-code/*" element={<SpassuCodeRoutesPage />} />
    <Route path="/orquestra" element={<OrquestraPage />} />
    <Route path="/gestao-fluxo" element={<GestaoFluxoPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
